import React, { useState } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

import { TRANSACTIONS_PAGE_SIZE } from '../../pages/constants';
import DatePresenter from '../../presenters/DatePresenter';
import { ACTIVE } from '../../presenters/TransactionPresenter';
import FastCashAdminPanel from '../../pages/FastCashAdminPanel';
import { StoreProvider } from '../../components';

const fastCashContainerQuery = graphql`
  query FastCashContainerQuery(
    $first: Int
    $after: String
    $byAuth0Id: String!
    $afterDate: Date!
    $beforeDate: Date!
    $order: String!
    $bankAccountIdEq: String
    $stateEq: String
    $storeIdEq: String
    $integrationItemIdEq: Int
    $nameCont: String
    $byMerchantNames: String
    $nameNotIContAll: String
  ) {
    fastCash(byAuth0Id: $byAuth0Id) {
      ...FastCashContainer_fastCash
    }
    fastCashLists {
      ...FastCashContainer_fastCashLists
        @arguments(
          first: $first
          after: $after
          byAuth0Id: $byAuth0Id
          afterDate: $afterDate
          beforeDate: $beforeDate
          order: $order
          bankAccountIdEq: $bankAccountIdEq
          stateEq: $stateEq
          storeIdEq: $storeIdEq
          integrationItemIdEq: $integrationItemIdEq
          nameCont: $nameCont
          byMerchantNames: $byMerchantNames
          nameNotIContAll: $nameNotIContAll
        )
    }
  }
`;

const FastCashFragmentContainer = createPaginationContainer(
  FastCashAdminPanel,
  {
    fastCash: graphql`
      fragment FastCashContainer_fastCash on FastCash {
        id
        lifetimeCash
        availableCash
        pendingCash
        specialCashbackRate {
          id
          fastCashId
          userId
          comment
          state
          cashbackRate
          createdAt
          updatedAt
          deletedAt
        }
        firstOnboardedAt
        blockedState
        transactionsData(
          afterDate: $afterDate
          beforeDate: $beforeDate
          stateEq: $stateEq
          bankAccountIdEq: $bankAccountIdEq
          storeIdEq: $storeIdEq
          integrationItemIdEq: $integrationItemIdEq
          nameCont: $nameCont
          byMerchantNames: $byMerchantNames
          nameNotIContAll: $nameNotIContAll
        ) {
          transactionsCount
          depositsAmount
          expensesAmount
        }
        cashbacksData(afterDate: $afterDate, beforeDate: $beforeDate) {
          netCashbacksAmount
          spentCashbacksAmount
          earnedCashbacksAmount
          refundCashbacksAmount
        }
        integrationItems {
          id
          institutionName
          itemId
          state
          errorCode
          accountOnboardedAt
        }
        user {
          email
          referrer {
            fullName
            auth0Id
          }
          invitedUsers {
            auth0Id
            fullName
          }
        }
        cashbacks(afterDate: $afterDate, beforeDate: $beforeDate) {
          id
          name
          amount
          state
          date
          quarantinedReason
          availableStates
          storeId
          place
          ...CashbackCell_cashback
        }
        bankAccounts {
          id
          name
          duplicationState
          plaidAccountId
          availableDuplicationStates
          bankAccountsWithMask {
            id
          }
          integrationItem {
            institutionName
          }
          ...BankAccountCell_bankAccount
        }
        checkIns(afterDate: $afterDate, beforeDate: $beforeDate) {
          id
          createdAt
        }
        latestMembershipState
      }
    `,
    fastCashLists: graphql`
      fragment FastCashContainer_fastCashLists on FastCashLists
      @argumentDefinitions(
        byAuth0Id: { type: "String!" }
        afterDate: { type: "Date!" }
        beforeDate: { type: "Date!" }
        order: { type: "String!" }
        first: { type: "Int" }
        after: { type: "String" }
        bankAccountIdEq: { type: "String" }
        stateEq: { type: "String" }
        storeIdEq: { type: "String" }
        integrationItemIdEq: { type: "Int" }
        nameCont: { type: "String" }
        byMerchantNames: { type: "String" }
        nameNotIContAll: { type: "String" }
      ) {
        id
        plaidTransactions(
          first: $first
          after: $after
          byAuth0Id: $byAuth0Id
          afterDate: $afterDate
          beforeDate: $beforeDate
          order: $order
          bankAccountIdEq: $bankAccountIdEq
          stateEq: $stateEq
          storeIdEq: $storeIdEq
          integrationItemIdEq: $integrationItemIdEq
          nameCont: $nameCont
          byMerchantNames: $byMerchantNames
          nameNotIContAll: $nameNotIContAll
        ) @connection(key: "FastCashContainer_plaidTransactions") {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              state
              date
              ...TransactionCell_transaction
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps: (props) => props.fastCashLists.plaidTransactions,
    getFragmentVariables: (previousVariables, totalCount) => {
      return {
        ...previousVariables,
        first: totalCount,
      };
    },
    getVariables: (_props, pageInfo, fragmentVariables) => {
      return {
        ...fragmentVariables,
        after: pageInfo.cursor,
        first: TRANSACTIONS_PAGE_SIZE,
      };
    },
    query: fastCashContainerQuery,
  }
);

const FastCashContainer = (props) => {
  const queryParams = queryString.parse(props.location.search);
  const { byAuth0Id, afterDate, beforeDate, order } = queryParams;

  const initialParams = () => {
    const variables = {
      byAuth0Id: byAuth0Id || '',
      afterDate: afterDate || DatePresenter.oneMonthAgo(),
      beforeDate: beforeDate || DatePresenter.today(),
      order: order || 'DESC',
      bankAccountIdEq: '',
      first: TRANSACTIONS_PAGE_SIZE,
      stateEq: ACTIVE,
    };

    return variables;
  };

  const [searchParams, setSearchParams] = useState(initialParams);

  const preprocessParams = () => {
    return searchParams;
  };

  return (
    <StoreProvider
      container={FastCashFragmentContainer}
      modern
      preprocessParams={preprocessParams}
      query={fastCashContainerQuery}
      searchParams={searchParams}
      onSearchParamsChange={setSearchParams}
      {...props}
    />
  );
};

export default withRouter(FastCashContainer);
